import { Button, Input } from '@zyxantech/tux'
import { AlertCircle, Eye, EyeOff, Lock, Mail } from 'lucide-react'
import { useState, type FormEvent } from 'react'
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom'
import { ROUTES } from '@/constants/routes'
import { useAuth } from '@/hooks/useAuth'
import { useAuthStore } from '@/store/auth.store'

interface FromState {
  from?: { pathname: string }
}

export function LoginPage() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const { login, isLoggingIn } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const dest = (location.state as FromState | null)?.from?.pathname ?? ROUTES.OVERVIEW

  if (isAuthenticated) return <Navigate to={dest} replace />

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!email.trim() || !password) {
      setError('Please enter both email and password.')
      return
    }
    setError(null)
    const result = await login(email, password)
    if (!result.ok) {
      setError(result.error ?? 'Login failed. Please try again.')
      return
    }
    navigate(dest, { replace: true })
  }

  return (
    <div className="w-full">
      <div className="mb-7">
        <h1 className="text-xl font-semibold text-text-primary">Sign in to TAP</h1>
        <p className="mt-1 text-[13px] text-text-muted">
          Enter your credentials to access the platform.
        </p>
      </div>

      <form onSubmit={(e) => void handleSubmit(e)} className="flex flex-col gap-4">
        <Input
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@company.com"
          autoComplete="username"
          leadingIcon={<Mail size={15} />}
        />

        <div>
          <label className="mb-1.5 block text-xs text-text-secondary" htmlFor="login-password">
            Password
          </label>
          <div className="relative">
            <Input
              id="login-password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              autoComplete="current-password"
              leadingIcon={<Lock size={15} />}
              className="pr-10"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              tabIndex={-1}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              className="absolute right-3 top-1/2 flex -translate-y-1/2 text-text-muted hover:text-text-secondary"
            >
              {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
            </button>
          </div>
        </div>

        {error && (
          <div className="flex items-start gap-2 rounded-md border border-danger bg-danger/10 px-3 py-2.5 text-xs leading-relaxed text-danger">
            <AlertCircle size={14} className="mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <Button type="submit" variant="primary" loading={isLoggingIn} className="mt-1 w-full">
          {isLoggingIn ? 'Signing in…' : 'Sign In'}
        </Button>
      </form>

      <div className="mt-6 text-center text-xs text-text-muted">
        <Link to={ROUTES.FORGOT_PASSWORD} className="text-accent hover:underline">
          Forgot your password?
        </Link>
      </div>
    </div>
  )
}
